/**
 * Hook for triggering exploit verification on Constantine findings.
 * Calls POST /constantine/exploit with selected risk keys.
 *
 * Tracks exploit lifecycle states:
 * - Ready: no exploit running, no results
 * - Running: exploit job in progress (mutation pending or polling active)
 * - Exploitable: exploit results show status "EXPLOITABLE"
 * - Not Exploitable: exploit results show status "COULD_NOT_EXPLOIT"
 * - Inconclusive: exploit results were inconclusive
 */
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useOsintContext } from '@/state/OsintContext';
import { ResourceType, Risk } from '@/types';

/** Exploit lifecycle states for the button UI */
export type ExploitState =
  | 'ready'
  | 'running'
  | 'exploitable'
  | 'not_exploitable'
  | 'inconclusive';

/** Check if a risk is a Constantine finding (name starts with constantine-) */
export function isConstantineFinding(risk: Risk): boolean {
  return risk.name.startsWith('constantine-');
}

/** Check if a risk already has exploit results via tags */
export function hasExploitResults(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return tags.some(
    t =>
      t === 'exploited' ||
      t === 'exploit-attempted' ||
      t === 'exploit-inconclusive'
  );
}

/** Check if a risk was successfully exploited (has 'exploited' tag) */
export function isExploitable(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return tags.includes('exploited');
}

/** Check if exploit was attempted but not successful (has 'exploit-attempted' but not 'exploited') */
export function isNotExploitable(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return tags.includes('exploit-attempted') && !tags.includes('exploited');
}

/** Check if exploit result was inconclusive (has 'exploit-inconclusive' tag) */
export function isInconclusive(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return tags.includes('exploit-inconclusive');
}

/**
 * Determine the aggregate exploit state for a set of risks.
 * - If any risk is exploitable, the aggregate is 'exploitable'
 * - If all have results but none exploitable, 'not_exploitable'
 * - If none have results, 'ready'
 */
export function getExploitState(risks: Risk[]): ExploitState {
  if (risks.length === 0) return 'ready';

  const allHaveResults = risks.every(hasExploitResults);

  const anyExploitable = risks.some(isExploitable);
  if (anyExploitable) return 'exploitable';

  const anyInconclusive = risks.some(isInconclusive);
  if (allHaveResults && anyInconclusive) return 'inconclusive';

  if (allHaveResults) return 'not_exploitable';

  return 'ready';
}

/** Returns label, icon, and className for the exploit button based on current state */
export function getExploitButtonConfig(state: ExploitState): {
  label: string;
  icon: ReactNode;
  className: string;
} {
  switch (state) {
    case 'running':
      return {
        label: 'Exploiting...',
        icon: <ArrowPathIcon className="size-4 animate-spin" />,
        className: 'bg-chariot-status-info text-white opacity-70 cursor-wait',
      };
    case 'exploitable':
      return {
        label: 'Exploitable',
        icon: <CheckCircleIcon className="size-4" />,
        className: 'bg-chariot-status-critical text-white',
      };
    case 'not_exploitable':
      return {
        label: 'Not Exploitable',
        icon: <XCircleIcon className="size-4" />,
        className: 'bg-chariot-status-info text-white',
      };
    case 'inconclusive':
      return {
        label: 'Inconclusive',
        icon: <ExclamationTriangleIcon className="size-4" />,
        className: 'bg-chariot-status-low text-white',
      };
    case 'ready':
    default:
      return {
        label: 'Exploit',
        icon: <ShieldExclamationIcon className="size-4" />,
        className: '',
      };
  }
}

export function useExploitVerification(onComplete?: () => void) {
  const { isOsint } = useOsintContext();
  const axios = useAxios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, []);

  const isRunning = isSubmitting || isPolling;

  const handleExploit = useCallback(
    async (vulnerabilities: Risk[]): Promise<void> => {
      const constantineFindings = vulnerabilities.filter(isConstantineFinding);

      if (constantineFindings.length === 0) {
        toast.warning('No Constantine findings selected');
        return;
      }

      const riskKeys = constantineFindings.map(r => r.key);
      const toastId = 'exploit-verification';

      toast.loading(
        `Starting exploit verification for ${riskKeys.length} finding${riskKeys.length > 1 ? 's' : ''}...`,
        { id: toastId }
      );

      setIsSubmitting(true);

      try {
        const exploitUrl = isOsint
          ? '/constantine/exploit?osint=true'
          : '/constantine/exploit';
        await axios.post(exploitUrl, {
          risk_keys: riskKeys,
        });

        toast.success(
          `Exploit verification started for ${riskKeys.length} finding${riskKeys.length > 1 ? 's' : ''}`,
          { id: toastId, duration: 5000 }
        );

        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Job),
        });
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(''),
        });

        // Start polling for risk updates
        setIsPolling(true);

        // Clear any existing polling
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

        pollingRef.current = setInterval(() => {
          queryClient.invalidateQueries({
            queryKey: getQueryKey.getMy(ResourceType.Risk),
          });
        }, 5000);

        // Stop polling after 30 minutes
        pollingTimeoutRef.current = setTimeout(() => {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setIsPolling(false);
        }, 1800000);

        onComplete?.();
      } catch (error) {
        console.error('[useExploitVerification] Failed:', error);
        toast.error('Failed to start exploit verification', { id: toastId });
      } finally {
        setIsSubmitting(false);
      }
    },
    [axios, isOsint, onComplete]
  );

  /** Stop polling early (e.g., when results arrive via risk refresh) */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  return { handleExploit, isRunning, stopPolling };
}
